/**
 * OC2-3562 — <PrescriptionWorkBundle> (renders one bullet for a 2+ work bundle)
 * Stateless functional component. No useState, no useEffect.
 */

import { getLinkedItems } from '@/components/contexts/KontentHomeConfigProvider'
import type { Mapping } from '@/types'
import type { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import { Fragment } from 'react'
import PrescriptionWork from './PrescriptionWork'
import type { PrescriptionWorkBundle as PrescriptionWorkBundleItem } from './types'

export interface PrescriptionWorkBundleProps {
	bundle: PrescriptionWorkBundleItem
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
}

export default function PrescriptionWorkBundle({
	bundle,
	linkedItems,
	mappings,
}: PrescriptionWorkBundleProps) {
	const innerWorks =
		getLinkedItems(bundle.elements.prescription_work, linkedItems) ?? []

	// Defensive: a bundle with no resolvable inner works is hollow — skip the
	// bullet entirely so the parent <PrescriptionGroup>'s <ul> does not render
	// an empty <li>. Aligns with DESIGN_SPEC §15.4.
	if (innerWorks.length === 0) {
		return null
	}

	return (
		<li
			data-testid="prescription-work-bundle"
			data-kontent-item-id={bundle.system.id}
		>
			{innerWorks.map((innerWork, index) => {
				const work = (
					<PrescriptionWork
						work={innerWork}
						linkedItems={linkedItems}
						mappings={mappings}
						showTypeLabel={true}
						inline
					/>
				)
				return (
					<Fragment key={innerWork.system.id}>
						{/* AND connector — block-level, indented further than the
						    work text per the design. */}
						{index > 0 && <div className="pl-4">AND</div>}
						{index === 0 ? work : <div>{work}</div>}
					</Fragment>
				)
			})}
		</li>
	)
}
